"use client";

import Image from "next/image";
import React from "react";

interface SmartImageProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src"> {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  fill?: boolean;
  sizes?: string;
  priority?: boolean;
  placeholder?: "blur" | "empty";
  blurDataURL?: string;
  className?: string;
}

/**
 * Smart Image component that automatically chooses between Next.js Image and regular img
 * based on the image source type
 */
export function SmartImage({
  src,
  alt,
  width,
  height,
  fill = false,
  sizes,
  priority = false,
  placeholder,
  blurDataURL,
  className,
  ..._props
}: SmartImageProps) {
  // Check if src is a blob URL or data URL (not supported by Next.js Image)
  const isBlobOrDataUrl =
    !!src && (src.startsWith("blob:") || src.startsWith("data:"));

  // Check if src is an external URL (not from our domain)
  const isExternalUrl =
    !!src &&
    (src.startsWith("http://") ||
      src.startsWith("https://") ||
      src.startsWith("//"));

  const shouldBypassOptimization = isBlobOrDataUrl || isExternalUrl;
  const passthroughLoader = ({ src: rawSrc }: { src: string }) => rawSrc;

  // For internal images, use Next.js Image component
  if (fill) {
    return (
      <Image
        src={src}
        alt={alt}
        fill
        loader={shouldBypassOptimization ? passthroughLoader : undefined}
        unoptimized={shouldBypassOptimization}
        sizes={
          sizes || "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
        }
        priority={priority}
        {...(placeholder !== undefined ? { placeholder } : {})}
        {...(blurDataURL !== undefined ? { blurDataURL } : {})}
        className={className}
        suppressHydrationWarning
      />
    );
  }

  return (
    <Image
      src={src}
      alt={alt}
      width={width || 300}
      height={height || 200}
      loader={shouldBypassOptimization ? passthroughLoader : undefined}
      unoptimized={shouldBypassOptimization}
      {...(sizes !== undefined ? { sizes } : {})}
      priority={priority}
      {...(placeholder !== undefined ? { placeholder } : {})}
      {...(blurDataURL !== undefined ? { blurDataURL } : {})}
      className={className}
      suppressHydrationWarning
    />
  );
}
